import { useCallback, useEffect, useState } from 'react';
import './BigBillionDays.css';
import heroBg from './assets/hero_bg.png';
import cardPhysical from './assets/card_physical.png';
import glowLg from './assets/glow_lg.svg';
import { fetchBbd, claimCardSpend } from './api';
import { loadCard, purchaseCard, offersBrands, lumpsumScreen, payBills, getSessionToken, listenForSession } from '../Helper/native';
import TopBanner from './components/TopBanner';
import Hero from './components/Hero';
import CampaignState from './components/CampaignState';
import OfferCard from './components/OfferCard';
import CardSpendSection from './components/CardSpendSection';
import SpendClaimPopup from './components/SpendClaimPopup';
import VouchersSection from './components/VouchersSection';
import BillsSection from './components/BillsSection';
import GoldSection from './components/GoldSection';
import FaqSection from './components/FaqSection';
import Cta from './components/Cta';

/* Sits where the first offer card goes: a pulsing placeholder while the
   campaign loads, or a retry card when the request fails. */
function OffersState({ failed, onRetry }) {
  if (failed) {
    return (
      <section className="bbd-glass bbd-offer bbd-offer-1">
        <p className="bbd-copy">Couldn't load the offers right now.</p>
        <Cta label="Retry" onClick={onRetry} />
      </section>
    );
  }
  return (
    <section className="bbd-glass bbd-offer bbd-offer-1" aria-busy="true">
      <div className="bbd-skeleton-line" />
      <div className="bbd-skeleton-line" />
      <div className="bbd-skeleton-pill" />
    </section>
  );
}

/* Big Billion Days campaign landing page (/bbd). Every section's copy, CTA
   and visibility comes from the campaign API (see api.js / mockResponse.js). */
export default function BigBillionDays() {
  const [model, setModel] = useState(null);      // mapped resp.bigBillionDays
  const [status, setStatus] = useState('loading'); // loading | ready | notStarted | ended | hidden | failed
  const [stateDate, setStateDate] = useState(null); // campaign start/end shown on the state screen
  const [claiming, setClaiming] = useState(false);   // ₹10k card-spend claim in flight
  const [claimed, setClaimed] = useState(false);     // reward already claimed this session
  const [claimFailed, setClaimFailed] = useState(false); // claim errored → keep CTA disabled
  const [claimPopup, setClaimPopup] = useState(null); // success response → drives the popup
  const [toast, setToast] = useState('');            // bottom toast message ('' = hidden)

  const load = useCallback(() => {
    let alive = true;
    setStatus('loading');
    /* Acquire the access token the same way the Landing (/) screen does, so a
       native deep-link straight to /bbd is authenticated before we fetch:
       capture it from the URL and from the native session bridge, then persist
       it to sessionStorage — the single source of truth apiFetch reads. */
    const url = new URL(window.location.href);
    const tokenHeader = url.searchParams.get('token');
    const refreshToken = url.searchParams.get('refreshToken');
    if (tokenHeader) sessionStorage.setItem('AccessToken', tokenHeader);
    if (refreshToken) sessionStorage.setItem('RefreshToken', refreshToken);
    getSessionToken(); // also reads window.__GOSATS_SESSION__ injected by native

    fetchBbd()
      .then((res) => {
        if (!alive || !res) return;
        if (res.phase === 'notStarted' || res.phase === 'ended') {
          setStateDate(res.date || null);
          setStatus(res.phase);
          return;
        }
        setModel(res.model || null);
        /* live model present → ready; otherwise nothing to show */
        setStatus(res.phase === 'live' && res.model ? 'ready' : 'hidden');
      })
      .catch((err) => {
        console.error('campaign fetch failed:', err);
        if (alive) setStatus('failed');
      });
    return () => { alive = false; };
  }, []);

  useEffect(load, [load]);

  /* The native app can inject the session asynchronously (postMessage) after
     mount; when the token arrives, re-run the load so the page authenticates. */
  useEffect(() => listenForSession(() => load()), [load]);

  /* dispatcher for the CTA actions (and their params) the API sends */
  const runAction = useCallback((action, params) => {
    const p = params || {};
    switch (action) {
      case 'ADD_MONEY': loadCard(); break;          // native card recharge
      case 'GET_CARD': purchaseCard(); break;        // native card compare / purchase
      case 'BUY_GOLD': lumpsumScreen(null, true); break; // native lumpsum gold purchase
      case 'VOUCHERS': offersBrands(); break;        // native Brands screen
      case 'PAY_BILLS': payBills(p.categoryName); break; // native BBPS screen, on a category when given
      default: break; // unknown actions are no-ops
    }
  }, []);

  /* Claims the ₹10k card-spend reward, then shows the BBD success popup. On an
     API error (`res.error`) or network failure we surface a bottom toast and
     disable the CTA so the user isn't stuck retrying a broken request. */
  const onClaimCardSpend = useCallback(() => {
    if (claiming || claimed || claimFailed) return;
    setClaiming(true);
    claimCardSpend()
      .then((res) => {
        if (!res || res.error) {
          setClaimFailed(true);
          setToast('Something went wrong');
          return;
        }
        setClaimed(true);
        setClaimPopup(res); // success response drives the popup copy
      })
      .catch((err) => {
        console.error('card-spend claim failed:', err);
        setClaimFailed(true);
        setToast('Something went wrong');
      })
      .finally(() => setClaiming(false));
  }, [claiming, claimed, claimFailed]);

  /* auto-dismiss the toast a few seconds after it appears */
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const ready = status === 'ready' && model;
  const showCard = !!(model && model.addMoney); // hero card belongs to the Add Money offer

  /* hero offers, in design order, minus the ones this user shouldn't see;
     bbd-offer-1 / bbd-offer-2 position the glow for whichever cards remain */
  const offers = ready
    ? [
      model.addMoney && { key: 'addMoney', data: model.addMoney },
      model.physicalCard && { key: 'physicalCard', data: model.physicalCard, art: cardPhysical },
    ].filter(Boolean)
    : [];

  /* hero layout modifiers:
     - bare    : no hero offers at all (collapse the tuck-up spacing)
     - nocard  : Add Money off but another offer shows, so the hero card is gone
                 — add its space back so the first offer clears the title */
  const heroClass = 'bbd-hero'
    + (offers.length ? '' : ' bbd-hero-bare')
    + (!showCard && offers.length ? ' bbd-hero-nocard' : '');

  /* outside the live window → themed "not started" / "ended" screen */
  if (status === 'notStarted' || status === 'ended') {
    return <CampaignState phase={status} date={stateDate} />;
  }

  /* nothing to show (no dates and campaign off) */
  if (status === 'hidden') return null;

  return (
    <div className="bbd-page">
      <div className="bbd-phone">
        <TopBanner text={model && model.banner ? model.banner.text : ''} endsAt={model ? model.endsOn : null} />

        <div className={heroClass}>
          <img className="bbd-hero-bg" src={heroBg} alt="" />
          <Hero showCard={showCard} />

          {!ready && <OffersState failed={status === 'failed'} onRetry={load} />}

          {offers.map((offer, i) => (
            <OfferCard
              key={offer.key}
              className={'bbd-offer-' + (i + 1)}
              art={offer.art}
              data={offer.data}
              onAction={runAction}
            />
          ))}
        </div>

        {ready && (
          <div className={'bbd-lower' + (offers.length ? '' : ' bbd-lower-bare')}>
            <img className="bbd-glow-lg" src={glowLg} alt="" />
            {model.cardSpend && (
              <CardSpendSection
                data={model.cardSpend}
                onAction={runAction}
                onClaim={onClaimCardSpend}
                claiming={claiming}
                claimed={claimed}
                claimDisabled={claimFailed}
              />
            )}
            {model.gold && <GoldSection data={model.gold} onAction={runAction} />}
            {model.vouchers && <VouchersSection data={model.vouchers} onAction={runAction} />}
            {model.bills && <BillsSection data={model.bills} onAction={runAction} />}
            <FaqSection />
          </div>
        )}
      </div>

      <SpendClaimPopup open={!!claimPopup} data={claimPopup} onClose={() => setClaimPopup(null)} />

      <div className={'bbd-toast' + (toast ? ' bbd-toast-show' : '')} role="alert" aria-live="assertive">
        {toast}
      </div>
    </div>
  );
}
